<script setup lang="ts">
import type { AccordionItem, FormSubmitEvent } from '#ui/types'
import { merge } from 'es-toolkit/compat'

const props = defineProps<{
  action: 'create' | 'edit'
  data: Partial<Product>
  onSubmit: (event: FormSubmitEvent<Partial<Product>>) => void
  openNestedCallback: (data: Partial<Product>) => void
  title?: string
  disabledFields?: (keyof Product | string)[]
  readonly?: boolean
  navigateToSubpage?: boolean
}>()

const router = useRouter()
const { user } = useUserSession()
const { moduleId, userModule } = useModules()
const slideover = useSlideover()

const inert = computed(() => props.readonly || props.data?.immutable || !['admin', 'edit'].includes(userModule.value?.role))
const simple = ref(true)

const state = ref<Partial<Product>>(props.data)

const products$ = useProducts()

const $suppliers = await useFetch<Supplier[]>('/api/db/suppliers', {
  query: {},
  default: () => [],
})
const $applications = await useFetch<Application[]>('/api/db/applications', {
  default: () => [],
})
const $categories = await useFetch<Category[]>('/api/db/categories', {
  query: {},
  default: () => [],
})

const utils = useProductsUtils({ $suppliers, $applications, $categories })

const items = computed<AccordionItem[]>(() => {
  if (['create'].includes(props.action) && simple.value) {
    return [{ slot: 'simple', label: 'Creación rápida', icon: 'i-mdi-lightning-bolt', defaultOpen: true }]
  }
  return [
    {
      slot: 'general',
      label: 'General',
      icon: 'i-mdi-information',
      defaultOpen: true,
    },
    {
      slot: 'details',
      label: 'Detalles',
      icon: ICONS.products,
      defaultOpen: true,
    },
    {
      slot: 'data',
      label: 'Datos adicionales',
      icon: 'i-mdi-code-json',
      defaultOpen: false,
    },
    {
      slot: 'fiscalData',
      label: 'Información fiscal',
      icon: 'i-mdi-bank',
      defaultOpen: true,
    },
  ].filter(item => !['data'].includes(item.slot) || state.value.data)
})

const onUpdateCategoryId = async (categoryId: string) => {
  const category = utils.maps.categoryId.value.get(categoryId)
  state.value.image = category?.image
  state.value.description = category?.description
}

if (['create'].includes(props.action) && props.data.categoryId) {
  onUpdateCategoryId(props.data.categoryId)
}

const loading = ref(false)

const onSubmitProduct = async (event: FormSubmitEvent<Partial<Product>>) => {
  event.data = merge(state.value, event.data) // WORKAROUND: UForm drops nested props on validation
  loading.value = true
  await props.onSubmit(event)
  props.navigateToSubpage && router.push(`/catalogs/products/${event.data.id}`)
}
</script>

<template>
  <UDashboardSlideover prevent-close :ui="{ width: 'min-w-[40vw]' }">
    <template #title>
      <SlideoverTitle :title="title" :inert="inert" />
    </template>
    <UForm
      id="form.products"
      :validate-on="['submit']"
      :schema="products$.schema"
      :state="state"
      :inert="inert"
      @error="onFormError"
      @submit="onSubmitProduct"
    >
      <UAccordion
        multiple
        :items="items"
        :ui="{ item: { base: 'py-1 px-2 space-y-2 text-sm' } }"
      >
        <template #simple>
          <UFormGroup
            label="Nombre"
            name="name"
            :required="isRequired(products$.schema, 'name')"
          >
            <InputText
              v-model.defaultcase="state.name"
              autofocus
              :disabled="disabledFields?.includes('name')"
              class="flex-1"
            />
          </UFormGroup>

          <UFormGroup
            label="Tipo"
            name="type"
            :required="isRequired(products$.schema, 'type')"
          >
            <SelectMenuBaseInfo
              v-model="state.type"
              :options="utils.options.type"
              :disabled="disabledFields?.includes('type')"
            />
          </UFormGroup>

          <UFormGroup
            label="Categoría"
            name="categoryId"
            :required="isRequired(products$.schema, 'categoryId')"
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'categories',
                idField: 'categoryId',
                utils: utils,
                options: utils.options.categoryId.value,
                disabled: disabledFields?.includes('categoryId'),
                onClickUpdate: onUpdateCategoryId,
                slideoverOpenCreateProps: {
                  data: { type: 'products' },
                  disabledFields: ['type'],
                },
                refresh: $categories.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>

          <SlideoverButtonExpand @click="simple = false" />
        </template>

        <template #general>
          <UFormGroup
            label="Nombre"
            name="name"
            :required="isRequired(products$.schema, 'name')"
          >
            <InputText
              v-model.defaultcase="state.name"
              autofocus
              :disabled="disabledFields?.includes('name')"
              class="flex-1"
            />
          </UFormGroup>

          <UFormGroup
            label="Tipo"
            name="type"
            :required="isRequired(products$.schema, 'type')"
          >
            <SelectMenuBaseInfo
              v-model="state.type"
              :options="utils.options.type"
              :disabled="disabledFields?.includes('type')"
            />
          </UFormGroup>

          <UFormGroup
            label="Categoría"
            name="categoryId"
            :required="isRequired(products$.schema, 'categoryId')"
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'categories',
                idField: 'categoryId',
                utils: utils,
                options: utils.options.categoryId.value,
                disabled: disabledFields?.includes('categoryId'),
                onClickUpdate: onUpdateCategoryId,
                slideoverOpenCreateProps: {
                  data: { type: 'products' },
                  disabledFields: ['type'],
                },
                refresh: $categories.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>

          <UFormGroup
            label="Proveedor"
            name="supplierId"
            :required="isRequired(products$.schema, 'supplierId')"
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'suppliers',
                idField: 'supplierId',
                utils: utils,
                options: utils.options.supplierId.value,
                disabled: disabledFields?.includes('supplierId'),
                slideoverOpenCreateProps: {
                  data: { type: 'supplier' },
                  disabledFields: ['type'],
                },
                refresh: $suppliers.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>

          <UFormGroup
            label="Fabricante"
            name="supplierIdManufacturer"
            :required="isRequired(products$.schema, 'supplierIdManufacturer')"
          >
            <SelectMenuBaseReference
              v-model="state"
              clearable
              v-bind="{
                tableName: 'suppliers',
                idField: 'supplierIdManufacturer',
                utils: utils,
                options: utils.options.supplierIdManufacturer.value,
                disabled: disabledFields?.includes('supplierIdManufacturer'),
                slideoverOpenCreateProps: {
                  data: { type: 'supplier' },
                  disabledFields: ['type'],
                },
                refresh: $suppliers.refresh,
                openNestedCallback: (data) => openNestedCallback({ ...props, data }).then(refresh),
              }"
            />
          </UFormGroup>
        </template>

        <template #details>
          <UFormGroup
            label="Aplicaciones"
            name="applicationIds"
            :required="isRequired(products$.schema, 'applicationIds')"
          >
            <SelectMenuBase
              v-model="state.applicationIds"
              :options="utils.options.applicationId.value"
              :disabled="disabledFields?.includes('applicationIds')"
              multiple
              class="flex-1"
            />
          </UFormGroup>

          <UFormGroup
            label="Imagen"
            name="image"
            :required="isRequired(products$.schema, 'image')"
          >
            <InputImage
              v-model="state.image"
              table-name="products"
              :record-id="state.id"
            />
          </UFormGroup>

          <UFormGroup label="Etiquetas" name="tags">
            <SelectMenuCreatableString
              v-model="state.tags"
              :options="state.tags"
              creatable
            />
          </UFormGroup>

          <UFormGroup label="Archivos" name="files">
            <InputFiles
              v-model="state.files"
              table-name="products"
              :record-id="state.id"
              accept="*"
            />
          </UFormGroup>

          <UFormGroup
            label="Descripción"
            name="description"
            :required="isRequired(products$.schema, 'description')"
          >
            <UTextarea
              v-model="state.description"
              resize
              :disabled="disabledFields?.includes('description')"
            />
          </UFormGroup>
        </template>

        <template #fiscalData>
          <UFormGroup
            label="Clave Producto (SAT)"
            name="fiscalData.satProductServiceKey"
            :required="isRequired(products$.schema, 'fiscalData.satProductServiceKey')"
            help="Consulta el catálogo de productos y servicios del SAT"
          >
            <InputBaseLink
              v-model="state.fiscalData!.satProductServiceKey"
              :disabled="disabledFields?.includes('fiscalData.satProductServiceKey')"
              href="https://www.sat.gob.mx/consultas/53693/catalogo-de-productos-y-servicios"
            />
          </UFormGroup>
        </template>

        <template #data>
          <FieldsetData v-model="state.data" :utils="utils" />
        </template>
      </UAccordion>
    </UForm>

    <template v-if="!inert" #footer>
      <div class="grid grid-cols-2 gap-2 w-full">
        <UButton
          color="gray"
          label="Cancelar"
          block
          @click="slideover.close()"
        />
        <UButton
          form="form.products"
          type="submit"
          label="Confirmar"
          color="primary"
          block
          :loading="loading"
        />
      </div>
    </template>
  </UDashboardSlideover>
</template>
